import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { api, img, useApi, slugify, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SingleImage, ImageManager } from "@/pages/admin/ImageManager";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { SpanishFields, cleanEs } from "@/pages/admin/SpanishFields";

const ES_FIELDS = [{ key: "name", label: "Nombre", type: "input" }, { key: "title", label: "Título (H1)", type: "input" }, { key: "intro", label: "Introducción", rows: 2 },
  { key: "content", label: "Contenido", rows: 10 }, { key: "cta_label", label: "Texto del botón", type: "input" }, { key: "seo_title", label: "Título SEO", type: "input" }, { key: "seo_description", label: "Descripción SEO", rows: 2 },
  { key: "highlights", label: "Puntos destacados en la página de inicio (uno por línea)", rows: 4 }];
const BLANK = { slug: "", kind: "area", name: "", title: "", intro: "", content: "", cover_image: "", keywords: [], seo_title: "", seo_description: "", gallery: [], cta_label: "", cta_url: "", order: 99, published: true, es: {} };
const base = (p) => (p.kind === "activity" ? "things-to-do" : "areas");

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="font-bold">{label}</Label>{children}</div>;

const PlaceForm = ({ place, onSaved }) => {
  const [f, setF] = useState({ ...place, kw: (place.keywords || []).join(", ") });
  const [ai, setAi] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const keywords = () => f.kw.split(",").map((s) => s.trim()).filter(Boolean);
  const draft = async () => {
    setAi(true);
    try {
      const { data } = await api.post(`/admin/places/${f.id || "000000000000000000000000"}/draft`, { name: f.name, kind: f.kind, keywords: keywords() });
      setF((x) => ({ ...x, ...data }));
      toast.success("AI draft ready - review it, then save");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAi(false);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    const { kw, ...rest } = f;
    const body = { ...rest, slug: f.slug || slugify(f.name), keywords: keywords(), es: cleanEs(f.es) };
    try {
      if (f.id) await api.put(`/admin/places/${f.id}`, body);
      else await api.post("/admin/places", body);
      toast.success("Page saved. Spanish updates automatically.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="place-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name (footer link)"><Input required value={f.name} onChange={set("name")} data-testid="place-field-name" /></Field>
        <Field label="Type">
          <select value={f.kind} onChange={set("kind")} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="place-field-kind">
            <option value="area">Area</option><option value="activity">Activity / Things to do</option>
          </select>
        </Field>
        <Field label="Page heading (H1)"><Input value={f.title} onChange={set("title")} data-testid="place-field-title" /></Field>
        <Field label="URL slug"><Input value={f.slug} placeholder={slugify(f.name)} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} data-testid="place-field-slug" /></Field>
      </div>
      <Field label="Match words (comma separated - used to show related homes, businesses and stories)"><Input value={f.kw} onChange={set("kw")} data-testid="place-field-keywords" /></Field>
      <SingleImage value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} testId="place-image-upload" />
      <button type="button" onClick={draft} disabled={ai || !f.name} className="btn-outline" data-testid="place-ai-draft">
        {ai ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {ai ? "Writing…" : "Write with AI (uses facts from your site)"}
      </button>
      <Field label="Intro"><Textarea rows={2} value={f.intro} onChange={set("intro")} data-testid="place-field-intro" /></Field>
      <Field label="Page text (## heading, - bullet, **bold**, [link](/for-rent))"><Textarea rows={12} value={f.content} onChange={set("content")} data-testid="place-field-content" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Button text (optional)"><Input value={f.cta_label || ""} onChange={set("cta_label")} data-testid="place-field-cta_label" /></Field>
        <Field label="Button link (https://…)"><Input value={f.cta_url || ""} onChange={set("cta_url")} data-testid="place-field-cta_url" /></Field>
      </div>
      {f.slug === "cortez-estates" && <Field label="Homepage card bullet points (one per line, shown beside Homes for Sale)"><Textarea rows={4} value={f.highlights || ""} onChange={set("highlights")} data-testid="place-field-highlights" /></Field>}
      <Field label="Photo gallery (optional)"><ImageManager images={f.gallery || []} onChange={(g) => setF({ ...f, gallery: g })} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="SEO title"><Input value={f.seo_title} onChange={set("seo_title")} data-testid="place-field-seo_title" /></Field>
        <Field label="SEO description"><Input value={f.seo_description} onChange={set("seo_description")} data-testid="place-field-seo_description" /></Field>
      </div>
      <label className="flex items-center gap-3 font-bold"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="place-field-published" /> Published (shown in footer)</label>
      <SpanishFields fields={f.slug === "cortez-estates" ? ES_FIELDS : ES_FIELDS.filter((x) => x.key !== "highlights")} source={f} value={f.es || {}} onChange={(es) => setF({ ...f, es })} testId="place-es" />
      <button type="submit" className="btn-primary w-full" data-testid="place-save">Save page</button>
    </form>
  );
};

const Row = ({ p, onEdit, onRemove }) => (
  <div className="flex items-center gap-4 border-b border-clay p-4 last:border-b-0" data-testid={`admin-place-row-${p.slug}`}>
    <img src={img(p.cover_image)} alt="" className={`h-14 w-20 shrink-0 rounded-lg object-cover ${p.published ? "" : "opacity-40"}`} />
    <div className="min-w-0 flex-1">
      <p className="truncate font-bold text-ink">{p.name}{!p.published && <span className="ml-2 text-xs text-moss">(hidden)</span>}</p>
      <p className="truncate text-sm font-semibold text-sage">/{base(p)}/{p.slug} · {p.content ? `${p.content.split(/\s+/).length} words` : "no text yet"}</p>
    </div>
    <a href={`/${base(p)}/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="View" data-testid={`place-view-${p.slug}`}><ExternalLink className="h-4 w-4" /></a>
    <button onClick={() => onEdit(p)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="Edit" data-testid={`place-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
    <button onClick={() => onRemove(p)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-destructive" aria-label="Delete" data-testid={`place-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

export default function AdminPlaces() {
  const { data, reload } = useApi("/admin/places", []);
  const [editing, setEditing] = useState(null);
  const remove = async (p) => {
    if (!window.confirm(`Delete the page "${p.name}"?`)) return;
    await api.delete(`/admin/places/${p.id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div data-testid="admin-places">
      <AdminHeader title="Area & Activity Pages">
        <button onClick={() => setEditing({ ...BLANK, order: data.length })} className="btn-primary" data-testid="place-new"><Plus className="h-4 w-4" /> New page</button>
      </AdminHeader>
      <p className="mb-6 text-sm text-moss">These pages are linked in the website footer. Related homes, rentals, businesses and stories appear on each page automatically using the match words.</p>
      {[["area", "Areas"], ["activity", "Things to Do"]].map(([k, title]) => (
        <div key={k} className="mb-8">
          <h2 className="mb-3 text-lg font-black text-forest">{title}</h2>
          <div className="overflow-hidden rounded-2xl border border-clay bg-white" data-testid={`places-list-${k}`}>
            {data.filter((p) => p.kind === k).map((p) => <Row key={p.id} p={p} onEdit={setEditing} onRemove={remove} />)}
          </div>
        </div>
      ))}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-forest">{editing?.id ? `Edit ${editing.name}` : "New page"}</DialogTitle></DialogHeader>
          {editing && <PlaceForm place={editing} onSaved={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
